
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import InstallationDatePicker from '@/components/InstallationDatePicker';

interface OrderDetails {
  customerName: string;
  offerType: 'standard' | 'premium' | 'exclusive';
  offerPrice: number;
  initialPayment: number;
  remainingPayment: number;
  email: string;
  phone: string;
  address: string;
  numberOfRooms?: number;
  roomAreas?: number[];
  totalSquareArea?: number;
}

interface SchedulingInfo {
  date: string;
  time: string;
  numberOfRooms?: number;
  squareArea?: number;
}

const PaymentSuccess = () => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [schedulingComplete, setSchedulingComplete] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);
  const [scheduledTime, setScheduledTime] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [schedulingInfo, setSchedulingInfo] = useState<SchedulingInfo | null>(null);
  const location = useLocation();
  
  useEffect(() => {
    // Try to get order details from sessionStorage
    const storedDetails = sessionStorage.getItem('orderDetails');
    if (storedDetails) {
      try {
        const parsedDetails = JSON.parse(storedDetails);
        setOrderDetails(parsedDetails);
        console.log("PaymentSuccess - loaded order details:", parsedDetails);
      } catch (error) {
        console.error("Error parsing order details:", error);
      }
    }
    
    // Log any transaction details from URL params
    const searchParams = new URLSearchParams(location.search);
    const transactionId = searchParams.get('transactionId');
    
    if (transactionId) {
      console.log("Transaction completed successfully:", transactionId);
    }
    
    // Check if scheduling info exists in sessionStorage
    const scheduleInfo = sessionStorage.getItem('schedulingInfo');
    if (scheduleInfo) {
      try {
        const parsedInfo = JSON.parse(scheduleInfo);
        setSchedulingInfo(parsedInfo);
        console.log("PaymentSuccess - loaded scheduling info:", parsedInfo);
        
        if (parsedInfo.date && parsedInfo.time) {
          setScheduledDate(parsedInfo.date);
          setScheduledTime(parsedInfo.time);
          setSchedulingComplete(true);
        }
      } catch (error) {
        console.error("Error parsing scheduling info:", error);
      }
    }
  }, [location]);
  
  const handleSchedulingComplete = (date: string, time: string) => {
    const roomCount = orderDetails?.numberOfRooms;
    const squareArea = orderDetails?.totalSquareArea;
    
    console.log("handleSchedulingComplete - saving scheduling info:", { 
      date, 
      time, 
      roomCount, 
      squareArea 
    });
    
    setScheduledDate(date);
    setScheduledTime(time);
    setSchedulingComplete(true);
    
    // Store scheduling info in sessionStorage
    const infoToStore = {
      date,
      time,
      numberOfRooms: roomCount,
      squareArea: squareArea
    };
    
    sessionStorage.setItem('schedulingInfo', JSON.stringify(infoToStore));
    setSchedulingInfo(infoToStore);
  };
  
  if (showDatePicker && orderDetails && !schedulingComplete) {
    // Explicitly handle room count and area
    const roomCount = orderDetails.numberOfRooms;
    const squareArea = orderDetails.totalSquareArea;
    
    console.log("Rendering InstallationDatePicker with values:", { 
      roomCount, 
      squareArea, 
      offerType: orderDetails.offerType,
      orderDetails
    });
    
    if (roomCount === undefined || squareArea === undefined) {
      console.error("Missing room count or square area in order details:", { roomCount, squareArea });
    }
    
    return (
      <InstallationDatePicker 
        selectedOffer={orderDetails.offerType}
        customerName={orderDetails.customerName}
        onSchedulingComplete={handleSchedulingComplete}
        numberOfRooms={roomCount || 0} // Provide fallback but log error if missing
        squareArea={squareArea || 0}    // Provide fallback but log error if missing
      />
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-6">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 space-y-6 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900">Zamówienie przyjęte!</h1>
        
        {orderDetails && (
          <div className="text-left bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="font-semibold mb-2">Szczegóły zamówienia:</h3>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Klient:</span> {orderDetails.customerName}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Pakiet:</span> {orderDetails.offerType === 'standard' ? 'Standard' : orderDetails.offerType === 'premium' ? 'Premium' : 'Exclusive'}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Wartość całkowita:</span> {orderDetails.offerPrice.toFixed(2)} zł
            </p>
            {orderDetails.numberOfRooms && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">Liczba pokoi:</span> {orderDetails.numberOfRooms}
              </p>
            )}
            {orderDetails.totalSquareArea && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">Powierzchnia:</span> {orderDetails.totalSquareArea.toFixed(2)} m²
              </p>
            )}
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-sm text-green-600 font-medium">Opłacona zaliczka (70%): {orderDetails.initialPayment?.toFixed(2)} zł</p>
              <p className="text-sm text-amber-600 font-medium">Do zapłaty po montażu (30%): {orderDetails.remainingPayment?.toFixed(2)} zł</p>
            </div>
          </div>
        )}
        
        {orderDetails && (
          <Alert className="bg-blue-50 border-blue-100 mb-4">
            <AlertTitle className="text-blue-800">Pozostała płatność</AlertTitle>
            <AlertDescription className="text-blue-700">
              Po pomyślnym montażu klimatyzacji otrzymasz informację o płatności pozostałych 30% wartości zamówienia ({orderDetails.remainingPayment?.toFixed(2)} zł).
            </AlertDescription>
          </Alert>
        )}
        
        {schedulingComplete && scheduledDate && scheduledTime ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-100 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-2">Montaż zaplanowany!</h3>
              <p className="text-sm text-green-700">
                <span className="font-medium">Data:</span> {scheduledDate}
              </p>
              <p className="text-sm text-green-700">
                <span className="font-medium">Godzina:</span> {scheduledTime}
              </p>
              {schedulingInfo?.numberOfRooms && (
                <p className="text-sm text-green-700">
                  <span className="font-medium">Liczba pokoi:</span> {schedulingInfo.numberOfRooms}
                </p>
              )}
              {schedulingInfo?.squareArea && (
                <p className="text-sm text-green-700">
                  <span className="font-medium">Powierzchnia:</span> {schedulingInfo.squareArea.toFixed(2)} m²
                </p>
              )}
              <p className="text-xs mt-3 text-green-600">
                Nasz zespół skontaktuje się z Tobą dzień przed montażem, aby potwierdzić wizytę.
              </p>
            </div>
            
            <Link to="/">
              <Button className="w-full">
                Wróć do strony głównej
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Dziękujemy za zamówienie. Teraz możesz wybrać termin montażu klimatyzacji.
            </p>
            
            <Button 
              className="w-full"
              onClick={() => setShowDatePicker(true)}
            >
              Zaplanuj montaż
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
